import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { RadioGroup, RadioGroupItem } from '@ooh/ui/components/radio-group';
import { ScopeChip } from '@ooh/ui/components/scope-chip';
import type { RoleScopeRow, ScopeOwnership, OfferDepartment } from '@ooh/contracts';
import {
  summarizeMediaTypeLabel,
  summarizeOwnershipLabel,
  summarizeDepartmentLabel,
  ownershipOptionCollides,
  departmentOptionCollides,
  OWNERSHIP_CANDIDATES,
  DEPARTMENT_CANDIDATES,
} from './grant-helpers';

/** Sentinel radio value standing in for the `null` (wildcard) scope option. */
const ALL_VALUE = '__all__';

export interface MediaTypeOption {
  code: string;
  label: string;
}

export interface ScopeGroupRowProps {
  /** Rows belonging to this one independent scope combination — they share ownership + department and may span several media types. */
  grants: RoleScopeRow[];
  /**
   * This group's index and the permission's full (flat) grant rows —
   * together they let `ownershipOptionCollides`/`departmentOptionCollides`
   * detect which ownership/department radio options would collide with a
   * sibling scope group, so those options can render disabled instead of
   * looking pickable and silently no-oping (see `withGroupOwnershipScope`).
   */
  groupIndex: number;
  permissionGrants: RoleScopeRow[];
  disabled: boolean;
  /** See `GrantRowProps.departmentScopeEditable` — same per-permission flag, threaded down to every group. */
  departmentScopeEditable: boolean;
  /** See `GrantRowProps.mediaTypeScopeEditable` — same per-permission flag, threaded down to every group. */
  mediaTypeScopeEditable: boolean;
  /** Full media-type catalog (data-driven — passed by the caller, not imported here). */
  mediaTypes: MediaTypeOption[];
  /** Raw (untranslated) permission name — used only to build the remove button's aria-label. */
  permissionName: string;
  onChangeMediaTypes: (mediaTypeCodes: string[] | null) => void;
  onChangeOwnership: (ownership: ScopeOwnership | null) => void;
  onChangeDepartment: (department: OfferDepartment | null) => void;
  onRemove: () => void;
}

/**
 * One independent scope combination (media types + ownership + department)
 * for a granted permission — the "nested" line `GrantRow` renders per
 * `groupGrantsByScope` group. Lets a permission like `supplier.view` hold
 * several unrelated scope rows at once (e.g. "large format + mural" at
 * RW+Broker, "billboard" restricted to RW only), each independently
 * editable via its own three scope chips and removable via its own trailing
 * "x" affordance. That "x" button's accessible name includes this group's
 * media-type/ownership summary (not just the shared permission name) so
 * sibling groups on the same permission stay distinguishable to
 * screen-reader users — see `permissionMatrix.grant.removeScopeAriaLabel`.
 */
export function ScopeGroupRow({
  grants,
  groupIndex,
  permissionGrants,
  disabled,
  departmentScopeEditable,
  mediaTypeScopeEditable,
  mediaTypes,
  permissionName,
  onChangeMediaTypes,
  onChangeOwnership,
  onChangeDepartment,
  onRemove,
}: ScopeGroupRowProps) {
  const { t } = useTranslation('admin');

  const currentMediaTypes = new Set(
    grants.some((g) => g.mediaType === null)
      ? []
      : grants.map((g) => g.mediaType).filter((v): v is string => v !== null),
  );
  const isAllMediaTypes = grants.some((g) => g.mediaType === null);

  const mediaTypeLabel = (code: string) => mediaTypes.find((mt) => mt.code === code)?.label ?? code;

  const ownershipLabel = (value: ScopeOwnership): string => {
    const key = value === 'RW' ? 'rwOnly' : 'brokerOnly';
    return t(`permissionMatrix.grant.ownership.${key}`);
  };

  const departmentLabel = (value: OfferDepartment): string => {
    const key = value === 'new_products' ? 'newProducts' : value;
    return t(`permissionMatrix.grant.department.${key}`);
  };

  /** Radio label for an ownership option, including the `null` (wildcard/"both") case. */
  const ownershipRadioLabel = (value: ScopeOwnership | null): string =>
    value === null ? t('permissionMatrix.grant.ownership.both') : ownershipLabel(value);

  /** Radio label for a department option, including the `null` (wildcard/"all") case. */
  const departmentRadioLabel = (value: OfferDepartment | null): string =>
    value === null ? t('permissionMatrix.grant.department.all') : departmentLabel(value);

  // Reused for both the chip labels below and the remove button's
  // accessible name — sibling groups on the same permission would otherwise
  // share an identical "Remove scope — {name}" label, making them
  // indistinguishable to screen-reader users (see `removeScopeAriaLabel`).
  // When media-type scope isn't API-enforced for this permission
  // (`mediaTypeScopeEditable=false`), the chip always shows the wildcard
  // label regardless of any stale saved media type — the aria-label mirrors
  // that same displayed value rather than the raw (possibly stale) data.
  const mediaTypeSummary = mediaTypeScopeEditable
    ? summarizeMediaTypeLabel(grants, mediaTypeLabel)
    : t('permissionMatrix.grant.mediaType.allOption');
  const ownershipSummary = summarizeOwnershipLabel(grants, ownershipLabel);

  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <div className="flex flex-wrap items-center gap-1.5 flex-1">
        <ScopeChip label={mediaTypeSummary} disabled={disabled || !mediaTypeScopeEditable}>
          <p className="text-xs font-semibold mb-2">
            {t('permissionMatrix.grant.mediaType.title')}
          </p>
          <label className="flex items-center gap-2 text-[13px] py-1 cursor-pointer">
            <Checkbox
              checked={isAllMediaTypes}
              onCheckedChange={(checked) =>
                onChangeMediaTypes(checked === true ? null : mediaTypes.map((mt) => mt.code))
              }
            />
            {t('permissionMatrix.grant.mediaType.allOption')}
          </label>
          <div className="my-2 border-t border-border/40" />
          <div className="pl-1 max-h-48 overflow-y-auto space-y-0.5">
            {mediaTypes.map((mt) => (
              <label
                key={mt.code}
                className={`flex items-center gap-2 text-[13px] py-0.5 ${
                  isAllMediaTypes ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                }`}
              >
                <Checkbox
                  checked={isAllMediaTypes || currentMediaTypes.has(mt.code)}
                  disabled={isAllMediaTypes}
                  onCheckedChange={(checked) => {
                    const next = new Set(currentMediaTypes);
                    if (checked === true) next.add(mt.code);
                    else next.delete(mt.code);
                    onChangeMediaTypes(Array.from(next));
                  }}
                />
                {mt.label}
              </label>
            ))}
          </div>
        </ScopeChip>

        <ScopeChip label={ownershipSummary} disabled={disabled}>
          {(close) => (
            <>
              <p className="text-xs font-semibold mb-2">
                {t('permissionMatrix.grant.ownership.title')}
              </p>
              <RadioGroup
                value={grants[0]?.ownership ?? ALL_VALUE}
                onValueChange={(value) => {
                  onChangeOwnership(value === ALL_VALUE ? null : (value as ScopeOwnership));
                  close();
                }}
              >
                {OWNERSHIP_CANDIDATES.map((ownership) => {
                  const radioValue = ownership ?? ALL_VALUE;
                  const collides = ownershipOptionCollides(permissionGrants, groupIndex, ownership);
                  const collisionHint = collides
                    ? t('permissionMatrix.grant.scopeCollisionHint')
                    : undefined;
                  return (
                    <DisabledHintTooltip key={radioValue} disabled={collides} hint={collisionHint}>
                      <RadioGroupItem
                        value={radioValue}
                        label={ownershipRadioLabel(ownership)}
                        disabled={collides}
                      />
                    </DisabledHintTooltip>
                  );
                })}
              </RadioGroup>
            </>
          )}
        </ScopeChip>

        <ScopeChip
          label={
            departmentScopeEditable
              ? summarizeDepartmentLabel(grants, departmentLabel)
              : t('permissionMatrix.grant.department.all')
          }
          disabled={disabled || !departmentScopeEditable}
        >
          {(close) => (
            <>
              <p className="text-xs font-semibold mb-2">
                {t('permissionMatrix.grant.department.title')}
              </p>
              <RadioGroup
                value={grants[0]?.department ?? ALL_VALUE}
                onValueChange={(value) => {
                  onChangeDepartment(value === ALL_VALUE ? null : (value as OfferDepartment));
                  close();
                }}
              >
                {DEPARTMENT_CANDIDATES.map((department) => {
                  const radioValue = department ?? ALL_VALUE;
                  const collides = departmentOptionCollides(
                    permissionGrants,
                    groupIndex,
                    department,
                  );
                  const collisionHint = collides
                    ? t('permissionMatrix.grant.scopeCollisionHint')
                    : undefined;
                  return (
                    <DisabledHintTooltip key={radioValue} disabled={collides} hint={collisionHint}>
                      <RadioGroupItem
                        value={radioValue}
                        label={departmentRadioLabel(department)}
                        disabled={collides}
                      />
                    </DisabledHintTooltip>
                  );
                })}
              </RadioGroup>
            </>
          )}
        </ScopeChip>
      </div>
      {!disabled && (
        <Button
          variant="ghost"
          size="xs"
          onClick={onRemove}
          className="text-muted-foreground hover:text-foreground shrink-0 px-1"
          aria-label={t('permissionMatrix.grant.removeScopeAriaLabel', {
            name: permissionName,
            scope: `${mediaTypeSummary}, ${ownershipSummary}`,
          })}
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}
